import { useState, useEffect, useMemo } from "react";
import { useAuth } from "../../context/AuthContext";
import api from "../../services/api";
import { imprimirComprobante } from "../../components/Comprobante";

const TIPOS_CAKE = ["torta_configurable", "precio_manual"];
const TIPOS_FIJO = ["precio_fijo", "precio_por_unidad", "paquete"];

const itemVacio = {
  producto_id: "",
  producto_nombre: "",
  tipo_venta: "torta_configurable",
  formato: "redonda",
  porcion: "",
  precio_base: "",
  precio_manual_extra: "",
  precio_total: 0,
  detalle_especial: "",
  mensaje_torta: "",
  decoracion: "ninguno",
  personalizacion: null,
  precio_personalizacion_manual: "",
  sabor: "",
  sabores_disponibles: [],
  opciones_disponibles: [],
  variedades: [],
  cantidad: 1,
  cantidad_paquete: null,
  precio_unitario: "",
  paquetes_disponibles: [],
};

const formatMiles = (valor) => {
  const limpio = valor.toString().replace(/\D/g, "");
  if (!limpio) return "";
  return parseInt(limpio).toLocaleString("es-CL");
};

const desformatear = (valor) => valor.toString().replace(/\D/g, "");

const obtenerPrecio = (precioRow, formato) => {
  if (!precioRow) return null;
  if (precioRow.precio != null) return precioRow.precio;
  if (formato === "cuadrada" && precioRow.precio_cuadrada != null) return precioRow.precio_cuadrada;
  if (formato === "redonda" && precioRow.precio_redonda != null) return precioRow.precio_redonda;
  return precioRow.precio_redonda ?? precioRow.precio_cuadrada ?? null;
};

const obtenerPreciosDisponibles = (preciosRaw, esVirtual) => {
  let filtrados = preciosRaw;
  if (esVirtual) {
    filtrados = preciosRaw.filter((p) => p.sabor_id === null || p.sabor_id === undefined);
  }
  const vistos = new Set();
  return filtrados.filter((p) => {
    if (vistos.has(p.porcion)) return false;
    vistos.add(p.porcion);
    return true;
  });
};

const formatOpcionPorcion = (p) => {
  const partes = [];
  if (p.precio_redonda != null) partes.push(`Redonda $${parseInt(p.precio_redonda).toLocaleString("es-CL")}`);
  if (p.precio_cuadrada != null) partes.push(`Cuadrada $${parseInt(p.precio_cuadrada).toLocaleString("es-CL")}`);
  if (p.precio != null) partes.push(`$${parseInt(p.precio).toLocaleString("es-CL")}`);
  if (p.cantidad_paquete) partes.push(`${p.cantidad_paquete} uds`);
  return `${p.porcion} porciones — ${partes.join(" / ")}`;
};

const OPCIONES_DECORACION = [
  { key: "ninguno", label: "Ninguno", icon: "—" },
  { key: "nina", label: "Niña", icon: "👧" },
  { key: "nino", label: "Niño", icon: "👦" },
  { key: "mujer", label: "Mujer", icon: "👩" },
  { key: "hombre", label: "Hombre", icon: "👨" },
];

const OPCIONES_FORMATO = [
  { key: "redonda", label: "Redonda", icon: "⚪" },
  { key: "cuadrada", label: "Cuadrada", icon: "⬛" },
];

const TRAMOS_PERSONALIZACION = [
  { min: 10, max: 30, label: "10 a 30 personas", precioSugerido: 7000 },
  { min: 40, max: 50, label: "40 a 50 personas", precioSugerido: 15000 },
];

const SABORES_TRADICIONALES = [
  "Moca", "Tres Leche", "Selva Negra", "Oreo", "Limón", "Frutilla",
  "Durazno", "Chocolate", "Chocolate Tres Leche", "Mango Tres Leche", "Piña",
];

function resolverTramoPersonalizacion(porcion) {
  const p = parseInt(porcion);
  if (!p || isNaN(p)) return null;
  return TRAMOS_PERSONALIZACION.find((t) => p >= t.min && p <= t.max) || null;
}

export default function NuevoPedido() {
  const { user } = useAuth();
  const [productos, setProductos] = useState([]);
  const [precios, setPrecios] = useState([]);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [busqueda, setBusqueda] = useState("");
  const [categoriaActiva, setCategoriaActiva] = useState("todos");
  const [previsualizando, setPrevisualizando] = useState(false);

  const [cliente, setCliente] = useState({
    cliente_nombre: "",
    cliente_telefono: "",
    fecha_entrega: "",
    hora_entrega: "",
    abono: "",
    metodo_pago: "efectivo",
    atendido_por: user?.nombre || "",
    local_origen: user?.local || "",
  });

  const [carrito, setCarrito] = useState([]);
  const [itemActual, setItemActual] = useState({ ...itemVacio });

  useEffect(() => {
    cargarProductos();
  }, []);

  const cargarProductos = async () => {
    try {
      const res = await api.get("/productos");
      setProductos(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  const categorias = useMemo(() => {
    const cats = new Set(productos.map((p) => p.categoria).filter(Boolean));
    return ["todos", ...Array.from(cats)];
  }, [productos]);

  const productosFiltrados = useMemo(() => {
    return productos.filter((p) => {
      const matchCategoria = categoriaActiva === "todos" || p.categoria === categoriaActiva;
      const matchBusqueda = p.nombre.toLowerCase().includes(busqueda.toLowerCase());
      return matchCategoria && matchBusqueda;
    });
  }, [productos, categoriaActiva, busqueda]);

  const contenidoGrilla = useMemo(() => {
    if (categoriaActiva !== "torta") {
      return { sections: [{ title: null, items: productosFiltrados }], esTortaCategory: false };
    }

    const tortaProduct = productos.find((p) => p.nombre === "Torta" && p.categoria === "torta");
    if (!tortaProduct) {
      return { sections: [{ title: null, items: [] }], esTortaCategory: true };
    }

    const tradicionales = (tortaProduct.sabores || [])
      .filter((s) => s.tipo === "tradicional" || SABORES_TRADICIONALES.includes(s.nombre))
      .filter((s) => !busqueda || s.nombre.toLowerCase().includes(busqueda.toLowerCase()))
      .map((s) => ({
        _virtual: true,
        id: tortaProduct.id,
        nombre: s.nombre,
        categoria: "torta",
        tipo_venta: tortaProduct.tipo_venta,
      }));

    const especiales = productos
      .filter((p) => p.categoria === "torta" && p.nombre !== "Torta")
      .filter((p) => !busqueda || p.nombre.toLowerCase().includes(busqueda.toLowerCase()));

    const sections = [];
    if (tradicionales.length > 0) sections.push({ title: "TORTAS TRADICIONALES", items: tradicionales });
    if (especiales.length > 0) sections.push({ title: "TORTAS ESPECIALES", items: especiales });

    return { sections, esTortaCategory: true };
  }, [productos, categoriaActiva, busqueda, productosFiltrados]);

  const esTipoCake = TIPOS_CAKE.includes(itemActual.tipo_venta);
  const esTipoFijo = TIPOS_FIJO.includes(itemActual.tipo_venta);

  useEffect(() => {
    if (esTipoFijo) {
      const unit = parseInt(itemActual.precio_unitario) || 0;
      const cant = parseInt(itemActual.cantidad) || 1;
      setItemActual((it) => ({ ...it, precio_total: unit * cant }));
    } else {
      const base = parseInt(itemActual.precio_base) || 0;
      const extraManual = parseInt(itemActual.precio_manual_extra) || 0;
      const precioPersManual = parseInt(itemActual.precio_personalizacion_manual) || 0;
      const extra = precioPersManual || 0;
      setItemActual((it) => ({ ...it, precio_total: base + extra + extraManual }));
    }
  }, [
    itemActual.precio_base,
    itemActual.precio_manual_extra,
    itemActual.precio_personalizacion_manual,
    itemActual.precio_unitario,
    itemActual.cantidad,
    esTipoFijo,
  ]);

  const handleSeleccionarProducto = async (producto) => {
    if (producto._virtual) {
      const realProduct = productos.find((p) => p.id === producto.id);
      setItemActual({
        ...itemVacio,
        producto_id: producto.id,
        producto_nombre: realProduct?.nombre || "Torta",
        tipo_venta: producto.tipo_venta || "torta_configurable",
        sabor: producto.nombre,
        sabores_disponibles: realProduct?.sabores || [],
      });
      setPrecios([]);
      try {
        const res = await api.get(`/productos/${producto.id}/precios`);
        setPrecios(obtenerPreciosDisponibles(res.data, true));
      } catch (err) {
        console.error(err);
      }
      return;
    }

    const tipoVenta = producto.tipo_venta || "torta_configurable";
    setItemActual({
      ...itemVacio,
      producto_id: producto.id,
      producto_nombre: producto.nombre,
      tipo_venta: tipoVenta,
      sabores_disponibles: producto.sabores || [],
      opciones_disponibles: producto.opciones || [],
    });
    setPrecios([]);
    try {
      const res = await api.get(`/productos/${producto.id}/precios`);
      setPrecios(res.data);
      if (TIPOS_FIJO.includes(tipoVenta) && res.data.length > 0) {
        const paquetes = res.data
          .filter((r) => r.cantidad_paquete != null)
          .map((r) => ({ cantidad_paquete: r.cantidad_paquete, precio: obtenerPrecio(r, "redonda") ?? obtenerPrecio(r, "cuadrada") ?? r.precio ?? 0, id: r.id }))
          .sort((a, b) => a.cantidad_paquete - b.cantidad_paquete);
        if (paquetes.length > 0) {
          const primero = paquetes[0];
          setItemActual((it) => ({
            ...it,
            paquetes_disponibles: paquetes,
            cantidad_paquete: primero.cantidad_paquete,
            precio_unitario: primero.precio,
            precio_total: primero.precio * (parseInt(it.cantidad) || 1),
          }));
        } else {
          const p = res.data[0];
          setItemActual((it) => ({
            ...it,
            porcion: p.porcion,
            precio_unitario: obtenerPrecio(p, it.formato) ?? 0,
            cantidad_paquete: p.cantidad_paquete ?? null,
          }));
        }
      }
    } catch (err) {
      console.error(err);
    }
  };

  const handlePorcion = (porcion) => {
    const precio = precios.find((p) => p.porcion === parseInt(porcion));
    let formato = itemActual.formato;
    if (precio) {
      const disponibles = OPCIONES_FORMATO.filter((f) =>
        f.key === "cuadrada" ? precio.precio_cuadrada != null : precio.precio_redonda != null
      );
      if (disponibles.length > 0 && !disponibles.some((f) => f.key === formato)) {
        formato = disponibles[0].key;
      }
    }

    const tramo = resolverTramoPersonalizacion(porcion);
    let nuevaPersonalizacion = itemActual.personalizacion;
    let nuevoPrecioPersManual = itemActual.precio_personalizacion_manual;

    if (!tramo) {
      nuevaPersonalizacion = null;
      nuevoPrecioPersManual = "";
    } else {
      if (nuevaPersonalizacion === null || nuevaPersonalizacion === undefined) {
        nuevaPersonalizacion = `auto_${tramo.min}_${tramo.max}`;
        nuevoPrecioPersManual = tramo.precioSugerido;
      }
    }

    setItemActual({
      ...itemActual,
      porcion,
      formato,
      precio_base: precio ? obtenerPrecio(precio, formato) ?? "" : "",
      personalizacion: nuevaPersonalizacion,
      precio_personalizacion_manual: nuevoPrecioPersManual,
    });
  };

  const handleFormato = (formato) => {
    const precio = precios.find((p) => p.porcion === parseInt(itemActual.porcion));
    setItemActual({
      ...itemActual,
      formato,
      precio_base: precio ? obtenerPrecio(precio, formato) ?? "" : itemActual.precio_base,
    });
  };

  const handlePaquete = (cantidadPaquete) => {
    const paq = itemActual.paquetes_disponibles.find((p) => p.cantidad_paquete === parseInt(cantidadPaquete));
    if (!paq) return;
    setItemActual({
      ...itemActual,
      cantidad_paquete: paq.cantidad_paquete,
      precio_unitario: paq.precio,
      precio_total: paq.precio * (parseInt(itemActual.cantidad) || 1),
    });
  };

  const formatosDisponibles = useMemo(() => {
    const precio = precios.find((p) => p.porcion === parseInt(itemActual.porcion));
    if (!precio) return OPCIONES_FORMATO;
    const disponibles = OPCIONES_FORMATO.filter((f) =>
      f.key === "cuadrada" ? precio.precio_cuadrada != null : precio.precio_redonda != null
    );
    return disponibles.length > 0 ? disponibles : OPCIONES_FORMATO;
  }, [precios, itemActual.porcion]);

  const toggleVariedad = (nombre) => {
    setItemActual((it) => ({
      ...it,
      variedades: it.variedades.includes(nombre)
        ? it.variedades.filter((v) => v !== nombre)
        : [...it.variedades, nombre],
    }));
  };

  const itemCompleto = esTipoFijo
    ? itemActual.producto_id && itemActual.cantidad > 0 && itemActual.precio_unitario > 0
    : itemActual.producto_id && itemActual.porcion &&
      (itemActual.sabores_disponibles.length > 0 ? !!itemActual.sabor : true);

  const agregarAlCarrito = () => {
    if (!itemCompleto) return;
    setCarrito([...carrito, { ...itemActual }]);
    setItemActual({ ...itemVacio });
    setPrecios([]);
  };

  const quitarDelCarrito = (index) => {
    setCarrito(carrito.filter((_, i) => i !== index));
  };

  const totalCarrito = carrito.reduce((sum, item) => sum + (item.precio_total || 0), 0);
  const abonoNum = parseInt(desformatear(cliente.abono)) || 0;
  const saldoTotal = totalCarrito - abonoNum;

  const clienteCompleto =
    cliente.cliente_nombre &&
    cliente.cliente_telefono &&
    cliente.fecha_entrega &&
    cliente.hora_entrega &&
    cliente.local_origen &&
    cliente.local_origen.trim() !== "" &&
    carrito.length > 0;

  const abrirPrevisualizacion = () => {
    if (!clienteCompleto) return;
    setPrevisualizando(true);
  };

  const construirItemPayload = (item) => {
    const base = {
      producto_id: item.producto_id,
      producto: item.producto_nombre,
      tipo_venta: item.tipo_venta,
      precio_total: item.precio_total,
      precio_acordado: true,
    };

    if (TIPOS_FIJO.includes(item.tipo_venta)) {
      return {
        ...base,
        cantidad: item.cantidad || 1,
        cantidad_paquete: item.cantidad_paquete || null,
        precio_unitario: parseInt(item.precio_unitario) || 0,
        variedades: item.variedades && item.variedades.length > 0 ? item.variedades : null,
      };
    }

    if (item.tipo_venta === "precio_manual") {
      return {
        ...base,
        formato: item.formato,
        porcion: item.porcion,
        sabor: item.sabor || null,
        cantidad: 1,
        precio_unitario: item.precio_total,
        detalle_especial: item.detalle_especial,
        mensaje_torta: item.mensaje_torta,
        decoracion: item.decoracion,
        personalizaciones: item.personalizacion != null
          ? [{
              id: null,
              nombre: typeof item.personalizacion === "string" && item.personalizacion.startsWith("auto_")
                ? `Personalización con topper o papel de azúcar — ${resolverTramoPersonalizacion(item.porcion)?.label || ""}`
                : item.personalizacion,
              precio_manual: parseInt(item.precio_personalizacion_manual) || 0,
            }]
          : [],
        es_personalizada: item.personalizacion != null,
      };
    }

    const personalizacionesPayload = item.personalizacion != null
      ? [{
          id: typeof item.personalizacion === "number" ? item.personalizacion : null,
          nombre: typeof item.personalizacion === "string" && item.personalizacion.startsWith("auto_")
            ? `Personalización con topper o papel de azúcar — ${resolverTramoPersonalizacion(item.porcion)?.label || ""}`
            : typeof item.personalizacion === "string" && item.personalizacion.startsWith("manual_")
              ? `Personalización con topper o papel de azúcar — ${resolverTramoPersonalizacion(item.porcion)?.label || ""}`
              : item.personalizacion,
          precio_manual: parseInt(item.precio_personalizacion_manual) || 0,
        }]
      : [];

    return {
      ...base,
      formato: item.formato,
      porcion: item.porcion,
      sabor: item.sabor || null,
      detalle_especial: item.detalle_especial,
      mensaje_torta: item.mensaje_torta,
      decoracion: item.decoracion,
      personalizaciones: personalizacionesPayload,
      es_personalizada: personalizacionesPayload.length > 0,
    };
  };

  const confirmarEnvio = async () => {
    setLoading(true);
    try {
      const payload = {
        cliente_nombre: cliente.cliente_nombre,
        cliente_telefono: cliente.cliente_telefono,
        fecha_entrega: cliente.fecha_entrega,
        hora_entrega: cliente.hora_entrega,
        atendido_por: cliente.atendido_por,
        local_origen: cliente.local_origen.trim(),
        abono_total: abonoNum,
        metodo_pago: cliente.metodo_pago,
        items: carrito.map(construirItemPayload),
      };

      const res = await api.post("/pedidos/lote", payload);
      imprimirComprobante(res.data);
      setSuccess(true);
      setPrevisualizando(false);
      setCarrito([]);
      setCliente({
        cliente_nombre: "",
        cliente_telefono: "",
        fecha_entrega: "",
        hora_entrega: "",
        abono: "",
        metodo_pago: "efectivo",
        atendido_por: user?.nombre || "",
        local_origen: user?.local || "",
      });
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      console.error("Error al enviar pedido:", err.response?.data || err.message);
      const mensajeError = err.response?.data?.message || err.response?.data?.errors || "Error desconocido al enviar el pedido";
      alert(`Error: ${JSON.stringify(mensajeError)}`);
    }
    setLoading(false);
  };

  const inputClass =
    "w-full mt-1 bg-[#E4E9F2] border border-[#E5E9F0] rounded-lg px-3 py-2.5 text-gray-900 text-sm focus:outline-none focus:border-[#1A56DB] focus:ring-1 focus:ring-[#1A56DB] transition-all";
  const labelClass = "text-xs text-gray-500 font-medium uppercase tracking-wider";

  return (
    <div className="min-h-screen bg-[#E4E9F2]">
      <div className="bg-white border-b border-[#E5E9F0] px-4 py-2 flex items-center justify-between">
        <div className="flex items-center gap-2 text-sm text-gray-500">
          <span>Local:</span>
          <input
            type="text"
            value={cliente.local_origen}
            onChange={(e) => setCliente({ ...cliente, local_origen: e.target.value })}
            placeholder="Ej: Heroes, Centro, Zofri..."
            maxLength={100}
            className="bg-transparent border-b border-gray-300 focus:border-[#1A56DB] focus:outline-none text-gray-900 font-semibold text-sm px-1 py-0.5 w-48 transition-all"
          />
          <span className="text-gray-400">&middot;</span>
          <span className="text-gray-500">{user?.nombre}</span>
        </div>
      </div>

      {success && (
        <div className="bg-green-50 border-b border-green-200 px-4 py-3 text-green-700 font-medium text-sm text-center">
          ✅ Pedido enviado a producci&oacute;n exitosamente!
        </div>
      )}

      <div className="flex flex-col lg:flex-row gap-0 h-[calc(100vh-49px)]">
        {/* COLUMNA IZQUIERDA — Productos */}
        <div className="flex-1 flex flex-col p-4 overflow-hidden">
          <input
            type="text"
            value={busqueda}
            onChange={(e) => setBusqueda(e.target.value)}
            placeholder="Buscar producto..."
            className={`${inputClass} mb-3 mt-0`}
          />
          <div className="flex gap-2 mb-3 flex-wrap">
            {categorias.map((cat) => (
              <button
                key={cat}
                type="button"
                onClick={() => setCategoriaActiva(cat)}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium capitalize transition-all ${
                  categoriaActiva === cat
                    ? "bg-[#1A56DB] text-white"
                    : "bg-white text-gray-600 border border-[#E5E9F0] hover:border-[#1A56DB] hover:text-[#1A56DB]"
                }`}
              >
                {cat === "todos" ? "Todos" : cat}
              </button>
            ))}
          </div>
          <div className="overflow-y-auto flex-1">
            {contenidoGrilla.esTortaCategory ? (
              contenidoGrilla.sections.map((section, sIdx) => (
                <div key={sIdx} className="mb-3">
                  {section.title && (
                    <p className="text-[10px] font-bold text-gray-400 uppercase tracking-[0.15em] mb-2 px-0.5">
                      {section.title}
                    </p>
                  )}
                  <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-5 gap-2.5 content-start auto-rows-min">
                    {section.items.map((p) => {
                      const esVirtual = !!p._virtual;
                      const seleccionado = esVirtual
                        ? itemActual.producto_id === p.id && itemActual.sabor === p.nombre
                        : itemActual.producto_id === p.id;
                      return (
                        <button
                          key={esVirtual ? `v-${p.id}-${p.nombre}` : p.id}
                          type="button"
                          onClick={() => handleSeleccionarProducto(p)}
                          className={`text-left bg-white border rounded-xl p-3 transition-all hover:border-[#1A56DB] hover:shadow-md hover:-translate-y-0.5 ${
                            seleccionado ? "border-[#1A56DB] ring-2 ring-[#1A56DB]/20 shadow-md" : "border-[#E5E9F0]"
                          }`}
                        >
                          <div className="flex items-start justify-between mb-1">
                            <span className="text-xl leading-none">🎂</span>
                            {seleccionado && (
                              <span className="w-4 h-4 rounded-full bg-[#1A56DB] flex items-center justify-center flex-shrink-0">
                                <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3">
                                  <path d="M5 13l4 4L19 7" />
                                </svg>
                              </span>
                            )}
                          </div>
                          <div className="text-sm font-semibold text-gray-900 leading-tight">{p.nombre}</div>
                          <div className="text-xs text-gray-400 mt-0.5 capitalize">{p.categoria}</div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 content-start auto-rows-min">
                {productosFiltrados.map((p) => {
                  const iconoCategoria = {
                    torta: "🎂",
                    cheesecake: "🍰",
                    tarta: "🥧",
                    cocktail: "🍹",
                    coctel: "🍹",
                    queque: "🧁",
                    mousse: "🍮",
                    pie: "🥧",
                    pan: "🍞",
                    otro: "🍽️",
                  }[p.categoria] || "🎂";

                  const seleccionado = itemActual.producto_id === p.id;

                  return (
                    <button
                      key={p.id}
                      type="button"
                      onClick={() => handleSeleccionarProducto(p)}
                      className={`text-left bg-white border rounded-xl p-3.5 transition-all hover:border-[#1A56DB] hover:shadow-md hover:-translate-y-0.5 ${
                        seleccionado ? "border-[#1A56DB] ring-2 ring-[#1A56DB]/20 shadow-md" : "border-[#E5E9F0]"
                      }`}
                    >
                      <div className="flex items-start justify-between mb-1">
                        <span className="text-xl leading-none">{iconoCategoria}</span>
                        {seleccionado && (
                          <span className="w-4 h-4 rounded-full bg-[#1A56DB] flex items-center justify-center flex-shrink-0">
                            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3">
                              <path d="M5 13l4 4L19 7" />
                            </svg>
                          </span>
                        )}
                      </div>
                      <div className="text-sm font-semibold text-gray-900 leading-tight">{p.nombre}</div>
                      <div className="text-xs text-gray-400 mt-0.5 capitalize">{p.categoria}</div>
                    </button>
                  );
                })}
                {productosFiltrados.length === 0 && (
                  <div className="col-span-full text-center text-gray-400 py-12">No se encontraron productos</div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* COLUMNA DERECHA */}
        <div className="w-full lg:w-[420px] bg-white border-l border-[#E5E9F0] flex flex-col p-4 overflow-y-auto">
          {/* Datos del cliente */}
          <div className="mb-4 pb-4 border-b border-[#E5E9F0]">
            <h2 className="text-[#1A56DB] font-semibold text-sm mb-3 uppercase tracking-wider">Datos del cliente</h2>
            <div className="space-y-2">
              <input
                type="text"
                value={cliente.cliente_nombre}
                onChange={(e) => setCliente({ ...cliente, cliente_nombre: e.target.value })}
                placeholder="Nombre cliente"
                className={inputClass}
              />
              <input
                type="tel"
                value={cliente.cliente_telefono}
                onChange={(e) => setCliente({ ...cliente, cliente_telefono: e.target.value })}
                placeholder="Teléfono"
                className={inputClass}
              />
              <div className="grid grid-cols-2 gap-2">
                <input
                  type="date"
                  value={cliente.fecha_entrega}
                  onChange={(e) => setCliente({ ...cliente, fecha_entrega: e.target.value })}
                  className={inputClass}
                />
                <input
                  type="time"
                  value={cliente.hora_entrega}
                  onChange={(e) => setCliente({ ...cliente, hora_entrega: e.target.value })}
                  className={inputClass}
                />
              </div>
            </div>
          </div>

          {/* Item actual en armado */}
          <div className="mb-4 pb-4 border-b border-[#E5E9F0]">
            <h2 className="text-[#1A56DB] font-semibold text-sm mb-3 uppercase tracking-wider">
              {itemActual.producto_id ? `Agregando: ${itemActual.producto_nombre}${itemActual.sabor ? ` — ${itemActual.sabor}` : ""}` : "Selecciona un producto"}
            </h2>

            {itemActual.producto_id && esTipoCake && (
              <div className="space-y-3">
                <div>
                  <label className={labelClass}>Porciones</label>
                  <select
                    value={itemActual.porcion}
                    onChange={(e) => handlePorcion(e.target.value)}
                    className={inputClass}
                  >
                    <option value="">Porciones...</option>
                    {precios.map((p) => (
                      <option key={p.id} value={p.porcion}>
                        {formatOpcionPorcion(p)}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className={labelClass}>Formato de la torta</label>
                  <div className="grid grid-cols-2 gap-2 mt-1.5">
                    {formatosDisponibles.map((f) => (
                      <button
                        key={f.key}
                        type="button"
                        onClick={() => handleFormato(f.key)}
                        className={`py-3 rounded-lg text-xs font-medium border transition-all flex flex-col items-center gap-1 ${
                          itemActual.formato === f.key
                            ? "bg-[#1A56DB] text-white border-[#1A56DB]"
                            : "bg-[#E4E9F2] text-gray-500 border-[#E5E9F0] hover:border-[#1A56DB]"
                        }`}
                      >
                        <span className="text-xl leading-none">{f.icon}</span>
                        <span>{f.label}</span>
                      </button>
                    ))}
                  </div>
                </div>

                {itemActual.tipo_venta === "precio_manual" && itemActual.porcion && (
                  <div>
                    <label className={labelClass}>
                      Precio (referencia: ${(obtenerPrecio(precios.find((p) => p.porcion === parseInt(itemActual.porcion)), itemActual.formato) ?? 0).toLocaleString("es-CL")}, editable)
                    </label>
                    <input
                      type="text"
                      inputMode="numeric"
                      value={formatMiles(itemActual.precio_base)}
                      onChange={(e) => setItemActual({ ...itemActual, precio_base: desformatear(e.target.value) })}
                      placeholder="0"
                      className={inputClass}
                    />
                  </div>
                )}

                <div>
                  <label className={labelClass}>Decoración para</label>
                  <div className="grid grid-cols-3 gap-2 mt-1.5">
                    {OPCIONES_DECORACION.map((d) => (
                      <button
                        key={d.key}
                        type="button"
                        onClick={() => setItemActual({ ...itemActual, decoracion: d.key })}
                        className={`py-2.5 rounded-lg text-xs font-medium border transition-all flex flex-col items-center gap-0.5 ${
                          itemActual.decoracion === d.key
                            ? "bg-[#1A56DB] text-white border-[#1A56DB]"
                            : "bg-[#E4E9F2] text-gray-500 border-[#E5E9F0] hover:border-[#1A56DB]"
                        }`}
                      >
                        <span className="text-base leading-none">{d.icon}</span>
                        <span>{d.label}</span>
                      </button>
                    ))}
                  </div>
                </div>

                {itemActual.porcion && (
                  <div>
                    <label className={labelClass}>Personalización con topper o papel de azúcar</label>
                    <div className="space-y-1.5 mt-1.5">
                      <label className="flex items-center justify-between bg-[#E4E9F2] border border-[#E5E9F0] rounded-lg px-3 py-2 cursor-pointer">
                        <span className="flex items-center gap-2">
                          <input
                            type="radio"
                            name="personalizacion"
                            checked={itemActual.personalizacion === null}
                            onChange={() => setItemActual({ ...itemActual, personalizacion: null, precio_personalizacion_manual: "" })}
                            className="w-4 h-4 accent-[#1A56DB]"
                          />
                          <span className="text-sm text-gray-700">Ninguna</span>
                        </span>
                      </label>
                      {TRAMOS_PERSONALIZACION.map((tramo) => {
                        const key = `auto_${tramo.min}_${tramo.max}`;
                        const seleccionado = itemActual.personalizacion === key;
                        const precioEditable = seleccionado
                          ? (itemActual.precio_personalizacion_manual !== "" ? itemActual.precio_personalizacion_manual : tramo.precioSugerido)
                          : tramo.precioSugerido;
                        return (
                          <div key={key} className="bg-[#E4E9F2] border border-[#E5E9F0] rounded-lg px-3 py-2">
                            <label className="flex items-center justify-between cursor-pointer">
                              <span className="flex items-center gap-2">
                                <input
                                  type="radio"
                                  name="personalizacion"
                                  checked={seleccionado}
                                  onChange={() => setItemActual({
                                    ...itemActual,
                                    personalizacion: key,
                                    precio_personalizacion_manual: tramo.precioSugerido,
                                  })}
                                  className="w-4 h-4 accent-[#1A56DB]"
                                />
                                <span className="text-sm text-gray-700">{tramo.label}</span>
                              </span>
                              {seleccionado ? (
                                <div className="flex items-center gap-1">
                                  <span className="text-xs text-gray-400">+$</span>
                                  <input
                                    type="text"
                                    inputMode="numeric"
                                    value={formatMiles(precioEditable)}
                                    onChange={(e) => setItemActual({
                                      ...itemActual,
                                      precio_personalizacion_manual: desformatear(e.target.value),
                                    })}
                                    onClick={(e) => e.stopPropagation()}
                                    className="w-20 text-right text-xs font-medium text-gray-700 bg-white border border-gray-200 rounded px-1.5 py-0.5 focus:outline-none focus:border-[#1A56DB]"
                                  />
                                </div>
                              ) : (
                                <span className="text-xs font-medium text-gray-500">
                                  +${tramo.precioSugerido.toLocaleString("es-CL")}
                                </span>
                              )}
                            </label>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {itemActual.tipo_venta === "torta_configurable" && (
                  <div>
                    <label className={labelClass}>Ajuste manual de precio (opcional)</label>
                    <input
                      type="text"
                      inputMode="numeric"
                      value={formatMiles(itemActual.precio_manual_extra)}
                      onChange={(e) => setItemActual({ ...itemActual, precio_manual_extra: desformatear(e.target.value) })}
                      placeholder="0"
                      className={inputClass}
                    />
                  </div>
                )}

                <input
                  type="text"
                  value={itemActual.mensaje_torta}
                  onChange={(e) => setItemActual({ ...itemActual, mensaje_torta: e.target.value })}
                  placeholder='Mensaje: "Feliz cumpleaños María"'
                  className={inputClass}
                />
                <input
                  type="text"
                  value={itemActual.detalle_especial}
                  onChange={(e) => setItemActual({ ...itemActual, detalle_especial: e.target.value })}
                  placeholder="Detalle especial: sin crema, etc."
                  className={inputClass}
                />

                <button
                  type="button"
                  onClick={agregarAlCarrito}
                  disabled={!itemCompleto}
                  className="w-full bg-green-600 hover:bg-green-700 text-white font-medium py-2.5 rounded-lg transition-all disabled:opacity-40"
                >
                  + Agregar al pedido (${(itemActual.precio_total || 0).toLocaleString("es-CL")})
                </button>
              </div>
            )}

            {itemActual.producto_id && esTipoFijo && (
              <div className="space-y-3">
                {itemActual.paquetes_disponibles.length > 0 && (
                  <div>
                    <label className={labelClass}>Tamaño del paquete</label>
                    <div className="grid grid-cols-2 gap-2 mt-1.5">
                      {itemActual.paquetes_disponibles.map((pq) => (
                        <button
                          key={pq.cantidad_paquete}
                          type="button"
                          onClick={() => handlePaquete(pq.cantidad_paquete)}
                          className={`py-3 rounded-lg text-xs font-medium border transition-all flex flex-col items-center gap-0.5 ${
                            itemActual.cantidad_paquete === pq.cantidad_paquete
                              ? "bg-[#1A56DB] text-white border-[#1A56DB]"
                              : "bg-[#E4E9F2] text-gray-500 border-[#E5E9F0] hover:border-[#1A56DB]"
                          }`}
                        >
                          <span className="text-sm font-bold">{pq.cantidad_paquete} uds</span>
                          <span>${pq.precio.toLocaleString("es-CL")}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                <div>
                  <label className={labelClass}>Precio unitario (acordado)</label>
                  <input
                    type="text"
                    inputMode="numeric"
                    value={formatMiles(itemActual.precio_unitario)}
                    onChange={(e) => {
                      const val = desformatear(e.target.value);
                      const num = parseInt(val) || 0;
                      if (num >= 0) {
                        setItemActual({ ...itemActual, precio_unitario: val });
                      }
                    }}
                    placeholder="0"
                    className={inputClass}
                  />
                </div>

                {itemActual.cantidad_paquete && (
                  <div className="text-xs text-gray-400">
                    Paquete de {itemActual.cantidad_paquete} unidades
                    {itemActual.cantidad > 1 && (
                      <span> · {itemActual.cantidad} paquetes = {itemActual.cantidad * itemActual.cantidad_paquete} uds</span>
                    )}
                  </div>
                )}

                <div>
                  <label className={labelClass}>Cantidad de paquetes</label>
                  <input
                    type="number"
                    min="1"
                    value={itemActual.cantidad}
                    onChange={(e) => setItemActual({ ...itemActual, cantidad: Math.max(1, parseInt(e.target.value) || 1) })}
                    className={inputClass}
                  />
                </div>

                {(itemActual.opciones_disponibles.length > 0 || itemActual.sabores_disponibles.length > 0) && (
                  <div>
                    <label className={labelClass}>Variedades (opcional)</label>
                    <div className="space-y-1.5 mt-1.5 max-h-40 overflow-y-auto">
                      {[
                        ...itemActual.opciones_disponibles.map((v) => ({ ...v, _key: `o-${v.id}` })),
                        ...itemActual.sabores_disponibles.map((v) => ({ ...v, _key: `s-${v.id}` })),
                      ].map((v) => (
                        <label
                          key={v._key}
                          className="flex items-center gap-2 bg-[#E4E9F2] border border-[#E5E9F0] rounded-lg px-3 py-2 cursor-pointer"
                        >
                          <input
                            type="checkbox"
                            checked={itemActual.variedades.includes(v.nombre)}
                            onChange={() => toggleVariedad(v.nombre)}
                            className="w-4 h-4 accent-[#1A56DB]"
                          />
                          <span className="text-sm text-gray-700">{v.nombre}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                )}

                <button
                  type="button"
                  onClick={agregarAlCarrito}
                  disabled={!itemCompleto}
                  className="w-full bg-green-600 hover:bg-green-700 text-white font-medium py-2.5 rounded-lg transition-all disabled:opacity-40"
                >
                  + Agregar al pedido (${(itemActual.precio_total || 0).toLocaleString("es-CL")})
                </button>
              </div>
            )}
          </div>

          {/* Carrito */}
          <div className="flex-1">
            <h2 className="text-[#1A56DB] font-semibold text-sm mb-2 uppercase tracking-wider">
              Productos en este pedido ({carrito.length})
            </h2>

            {carrito.length === 0 && (
              <div className="text-center text-xs text-gray-400 py-6 bg-[#E4E9F2] rounded-lg border border-dashed border-gray-300">
                Aún no has agregado ningún producto
              </div>
            )}

            <div className="space-y-2 mb-3">
              {carrito.map((item, i) => (
                <div key={i} className="flex items-center justify-between bg-blue-50 border border-blue-200 rounded-lg px-3 py-2">
                  <div>
                    <div className="text-sm font-medium text-gray-900">
                      {item.producto_nombre}
                      {item.sabor && <span className="text-gray-500 font-normal"> — {item.sabor}</span>}
                    </div>
                    <div className="text-xs text-gray-500">
                      {TIPOS_FIJO.includes(item.tipo_venta)
                        ? item.cantidad_paquete
                          ? `${item.cantidad}×${item.cantidad_paquete} uds`
                          : `${item.cantidad} unid.`
                        : `${item.porcion} porc.`} · ${item.precio_total.toLocaleString("es-CL")}
                    </div>
                  </div>
                  <button onClick={() => quitarDelCarrito(i)} className="text-red-500 text-xs font-medium hover:underline">
                    Quitar
                  </button>
                </div>
              ))}
            </div>

            <div>
              <label className={labelClass}>Abono total ($)</label>
              <input
                type="text"
                inputMode="numeric"
                value={cliente.abono}
                onChange={(e) => setCliente({ ...cliente, abono: formatMiles(e.target.value) })}
                placeholder="0"
                className={inputClass}
              />
            </div>

            {parseInt(desformatear(cliente.abono)) > 0 && (
              <div>
                <label className={labelClass}>Método de pago</label>
                <div className="grid grid-cols-2 gap-2 mt-1.5">
                  {[
                    { key: "efectivo", label: "Efectivo", icon: "💵" },
                    { key: "tarjeta", label: "Tarjeta", icon: "💳" },
                  ].map((m) => (
                    <button
                      key={m.key}
                      type="button"
                      onClick={() => setCliente({ ...cliente, metodo_pago: m.key })}
                      className={`py-2.5 rounded-lg text-sm font-medium border transition-all flex items-center justify-center gap-2 ${
                        cliente.metodo_pago === m.key
                          ? "bg-[#1A56DB] text-white border-[#1A56DB]"
                          : "bg-[#E4E9F2] text-gray-500 border-[#E5E9F0] hover:border-[#1A56DB]"
                      }`}
                    >
                      <span>{m.icon}</span>
                      <span>{m.label}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div className="bg-[#E4E9F2] border border-[#E5E9F0] rounded-lg p-3 text-sm mt-3">
              <div className="flex justify-between font-semibold text-gray-900">
                <span>Total</span>
                <span>${totalCarrito.toLocaleString("es-CL")}</span>
              </div>
              <div className={`flex justify-between mt-1 ${saldoTotal > 0 ? "text-amber-600" : "text-green-600"}`}>
                <span>Saldo</span>
                <span>${saldoTotal.toLocaleString("es-CL")}</span>
              </div>
            </div>

            {!clienteCompleto && carrito.length > 0 && (
              <div className="text-xs text-amber-600 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mt-3">
                Falta completar: {[
                  !cliente.cliente_nombre && "nombre del cliente",
                  !cliente.cliente_telefono && "teléfono",
                  !cliente.fecha_entrega && "fecha de entrega",
                  !cliente.hora_entrega && "hora de entrega",
                  (!cliente.local_origen || cliente.local_origen.trim() === "") && "local de entrega",
                ].filter(Boolean).join(", ")}
              </div>
            )}

            <button
              type="button"
              onClick={abrirPrevisualizacion}
              disabled={!clienteCompleto}
              className="mt-3 w-full bg-[#1A56DB] hover:bg-[#1E429F] text-white font-semibold py-4 rounded-xl text-base transition-all disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Revisar y enviar a Producción
            </button>
          </div>
        </div>
      </div>

      {/* MODAL DE PREVISUALIZACIÓN */}
      {previsualizando && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center backdrop-blur-sm p-4">
          <div className="bg-white border border-[#E5E9F0] rounded-2xl p-6 w-full max-w-lg shadow-xl max-h-[90vh] overflow-y-auto">
            <h3 className="text-gray-900 font-semibold text-lg mb-1">Confirmar pedido</h3>
            <p className="text-gray-400 text-xs mb-4">Revisa que todo esté correcto antes de enviar a producción e imprimir</p>

            <div className="bg-[#E4E9F2] rounded-lg p-3 mb-3">
              <div className="text-sm font-semibold text-gray-900">{cliente.cliente_nombre}</div>
              <div className="text-xs text-gray-500">{cliente.cliente_telefono}</div>
              <div className="text-xs text-gray-500 mt-1">
                Entrega: {cliente.fecha_entrega} a las {cliente.hora_entrega}
              </div>
              <div className="text-xs text-gray-500 mt-1">
                Local: {cliente.local_origen?.toUpperCase() || "No especificado"}
              </div>
            </div>

            <div className="space-y-2 mb-3">
              {carrito.map((item, i) => {
                const decor = OPCIONES_DECORACION.find((d) => d.key === item.decoracion);
                const esFijo = TIPOS_FIJO.includes(item.tipo_venta);
                return (
                  <div key={i} className="border border-[#E5E9F0] rounded-lg p-3">
                    <div className="flex justify-between items-start">
                      <div>
                        <div className="text-sm font-semibold text-gray-900">{esFijo ? "🧁" : "🎂"} {item.producto_nombre}</div>
                        <div className="text-xs text-gray-500 mt-0.5">
                          {esFijo ? (
                            <>
                              {item.cantidad_paquete
                                ? `${item.cantidad}×${item.cantidad_paquete} uds`
                                : `${item.cantidad} unidad${item.cantidad === 1 ? "" : "es"}`}
                              {item.variedades && item.variedades.length > 0 && ` · ${item.variedades.join(", ")}`}
                            </>
                          ) : (
                            <>
                              {item.porcion} porciones · {item.formato}
                              {item.sabor && ` · ${item.sabor}`}
                              {decor && decor.key !== "ninguno" && ` · ${decor.label}`}
                            </>
                          )}
                        </div>
                        {item.mensaje_torta && <div className="text-xs text-gray-500 mt-0.5">Mensaje: &quot;{item.mensaje_torta}&quot;</div>}
                        {item.detalle_especial && <div className="text-xs text-gray-500 mt-0.5">Detalle: {item.detalle_especial}</div>}
                      </div>
                      <div className="text-sm font-semibold text-gray-900">${item.precio_total.toLocaleString("es-CL")}</div>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
              <div className="flex justify-between text-sm text-gray-700">
                <span>Total</span>
                <span className="font-semibold">${totalCarrito.toLocaleString("es-CL")}</span>
              </div>
              <div className="flex justify-between text-sm text-gray-700 mt-1">
                <span>Abono</span>
                <span>${abonoNum.toLocaleString("es-CL")}</span>
              </div>
              <div className="flex justify-between text-sm font-semibold mt-1 pt-1 border-t border-blue-200">
                <span>Saldo</span>
                <span className={saldoTotal > 0 ? "text-amber-600" : "text-green-600"}>
                  ${saldoTotal.toLocaleString("es-CL")}
                </span>
              </div>
            </div>

            <div className="flex gap-2">
              <button
                onClick={confirmarEnvio}
                disabled={loading}
                className="flex-1 bg-[#1A56DB] hover:bg-[#1E429F] text-white font-semibold py-3 rounded-lg transition-all disabled:opacity-50"
              >
                {loading ? "Enviando..." : "Confirmar e imprimir"}
              </button>
              <button
                onClick={() => setPrevisualizando(false)}
                className="bg-gray-100 hover:bg-gray-200 text-gray-600 px-4 rounded-lg transition-all"
              >
                Editar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
